import type { Ref } from 'vue';
import type { ExtendedPartTagType } from '@/entities/rubrics';
import { useI18n } from '@experience/i18n';
import ru from '@/widgets/rubricsBlock/i18n/ru.json';
import { allValue, moreValue } from '@/widgets/rubricsBlock/tour';

const MIN_TAGS_THRESHOLD = 3;
const FIRST_TAG_INDEX = 0;
const SECOND_TAG_INDEX = 1;

export function useTourCountryVisibleTags(
  rubricsAdapters: Ref<ExtendedPartTagType[]>,
  selectedValue: Ref<ExtendedPartTagType>,
  isMobile: Ref<boolean>,
) {
  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const getMobileTags = () => {
    if (selectedValue.value.value === allValue) {
      return [...rubricsAdapters.value];
    }

    const selectedTag = rubricsAdapters.value.find((tag) => tag.value === selectedValue.value.value);

    const tagToInsert = selectedTag || selectedValue.value;

    const filteredAdapters = rubricsAdapters.value.filter((tag) => tag.value !== selectedValue.value.value);

    if (filteredAdapters.length < MIN_TAGS_THRESHOLD) {
      return filteredAdapters.length > 0
        ? [filteredAdapters[FIRST_TAG_INDEX], tagToInsert, ...filteredAdapters.slice(FIRST_TAG_INDEX + 1)]
        : [tagToInsert];
    } else {
      return [
        filteredAdapters[FIRST_TAG_INDEX],
        filteredAdapters[SECOND_TAG_INDEX],
        tagToInsert,
        ...filteredAdapters.slice(SECOND_TAG_INDEX + 1),
      ];
    }
  };

  const getDesktopTags = () => {
    const isHasCurrentTag = rubricsAdapters.value.some((tag) => tag.value === selectedValue.value.value);

    const tags = isHasCurrentTag ? rubricsAdapters.value : [...rubricsAdapters.value, selectedValue.value];

    return [...tags, { label: t('rubrics_section.more'), value: moreValue }];
  };

  const visibleTags = computed(() => {
    if (selectedValue.value.value === moreValue) {
      return [];
    }

    return isMobile.value ? getMobileTags() : getDesktopTags();
  });

  return { visibleTags };
}
